import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Favicon edge length, in px. Matched to the line height of the source rows so the favicon and
 * the opens-in-new-window icon beside the title read as the same weight. Exported so that icon
 * stays pinned to the same value — the two are only "matched" while they share one constant.
 */
export const FAVICON_SIZE = 12;

/**
 * The icon sizes Sync Core actually renders, ascending.
 *
 * The endpoint serves these and nothing between them: asked for anything else it falls back to the
 * smallest, so a mark drawn at 28 px that asks for 56 gets a 16 px icon scaled up to fill it. That
 * is the one failure mode a caller cannot see coming — the request succeeds, the image loads, and
 * the only symptom is a blurry logo.
 */
export const FAVICON_REQUEST_SIZES = [16, 32, 64, 128, 256] as const;

/**
 * The size to ask Sync Core for, to draw a mark `size` px wide.
 *
 * Rounded UP to the next size served rather than to the nearest: rounding down is asking for an icon
 * with fewer pixels than the box it goes in, which is the blur this exists to avoid. Anything past
 * the largest served size takes that one, since the alternative is the 16 px fallback — the worst
 * answer available — and anything at or below the smallest takes the smallest.
 *
 * Kept as a function of the drawn size rather than pushed onto every caller: there is one call to
 * the endpoint in this file, and a caller that computed its own would be one refactor away from
 * computing a size the endpoint does not serve.
 */
export function faviconRequestSize(size: number): number {
  if (!Number.isFinite(size)) {
    return FAVICON_REQUEST_SIZES[0];
  }

  return FAVICON_REQUEST_SIZES.find((served) => served >= size) ?? FAVICON_REQUEST_SIZES[FAVICON_REQUEST_SIZES.length - 1];
}

/**
 * The one client call this component makes, as a typed slice of the API surface.
 *
 * Narrow on purpose: a test supplies exactly this and nothing else, with no cast, while the compiler
 * still checks the real `ISyncCoreApi` against it at every call site.
 */
export interface IFaviconUrlSource {
  utility: {
    scrape: {
      getFaviconUrl: (domain: string, size?: number) => string;
    };
  };
}

/**
 * Whether the screen a favicon renders on has its own critical data in hand yet.
 *
 * Sync Core still fetches and stores an icon it has not cached before synchronously, on the request
 * that asks for it — that can hold a connection for many seconds (46.5 s, measured on stage). Left
 * unbounded, that request races the same screen's own stats and term-list requests for the pod and
 * for the browser's per-origin connection budget — exactly the contention this gate exists to avoid.
 * A favicon is decoration; it must never be the thing a reader's real numbers are waiting behind.
 *
 * Defaults to `true` — load immediately — rather than `false`: a screen that renders a favicon
 * without wrapping it in `FaviconLoadGate` keeps the old, unblocked behaviour instead of silently
 * freezing on a placeholder forever.
 */
const FaviconLoadGateContext = createContext(true);

/**
 * Marks the point in a screen's tree below which favicons may start loading, once `ready` is true.
 *
 * `ready` is not a new concept a screen has to start tracking: every screen that renders a favicon
 * already has a boolean for "my own critical data has landed" — a bootstrap `loading` flag, a
 * summary-stats flag, whatever it already gates its own content on. Pass that straight through.
 * Nothing here starts a timer of its own, and nothing here is per-domain: it is one signal for
 * everything under it.
 */
export function FaviconLoadGate({ ready, children }: { ready: boolean; children: React.ReactNode }) {
  return <FaviconLoadGateContext.Provider value={ready}>{children}</FaviconLoadGateContext.Provider>;
}

/**
 * How many favicon loads may be in flight at once, across the whole page.
 *
 * A browser reserves on the order of six connections per origin, and that origin — Sync Core — is
 * also still answering the screen's own requests even after the gate opens (nothing here staggers
 * those). Four leaves headroom on that budget for anything the screen still needs, while still being
 * enough that a page of rows fills in within a couple of queue turns rather than one at a time. It
 * also caps how many of Sync Core's synchronous, multi-second cache-miss fetches one page can trigger
 * simultaneously — the same knob, read the other way round.
 */
export const FAVICON_MAX_CONCURRENT_LOADS = 4;

let activeFaviconLoads = 0;
const queuedFaviconLoads: Array<() => void> = [];

function admitQueuedFaviconLoads(): void {
  while (activeFaviconLoads < FAVICON_MAX_CONCURRENT_LOADS && queuedFaviconLoads.length) {
    const grant = queuedFaviconLoads.shift();
    if (!grant) {
      break;
    }
    activeFaviconLoads += 1;
    grant();
  }
}

/**
 * Enters one favicon load into the shared queue. `onGranted` fires once a slot is free — synchronously
 * when one already is, so a page with room shows its icons immediately rather than one tick later.
 *
 * The caller must call the returned function exactly once no matter what happens next: once the load
 * has settled (loaded or failed), or on unmount / a changed domain while still only queued. A slot
 * that is granted and never released starves every other favicon on the page for good.
 */
function requestFaviconLoadSlot(onGranted: () => void): () => void {
  let settled = false;
  let granted = false;
  const grant = () => {
    granted = true;
    onGranted();
  };
  queuedFaviconLoads.push(grant);
  admitQueuedFaviconLoads();

  return () => {
    if (settled) {
      return;
    }
    settled = true;
    if (granted) {
      activeFaviconLoads -= 1;
      admitQueuedFaviconLoads();
      return;
    }
    const index = queuedFaviconLoads.indexOf(grant);
    if (index >= 0) {
      queuedFaviconLoads.splice(index, 1);
    }
  };
}

/**
 * Drops every in-flight and queued favicon load. Exported for tests, which need each case to start
 * from an empty queue rather than inheriting whatever the previous case left behind.
 */
export function resetFaviconLoadQueue(): void {
  activeFaviconLoads = 0;
  queuedFaviconLoads.length = 0;
}

type FaviconLoadPhase = 'pending' | 'loading' | 'loaded' | 'failed';

/**
 * The favicon of a cited source, rendered left of its title.
 *
 * Sync Core proxies and caches the icon, so the browser never talks to the cited host directly — but
 * an icon it has not cached yet is still fetched and stored there and then, synchronously, and that
 * can take many seconds. So this never asks for one until its screen's `FaviconLoadGate` says the
 * screen's own critical data has landed, and even then shares `FAVICON_MAX_CONCURRENT_LOADS` across
 * every favicon on the page rather than firing all of them at once. Until a slot is granted, and for
 * a domain with no resolvable icon, the row keeps a blank box of the same size — never a
 * broken-image glyph, never a size change the reader can see happen. A failed load is final for this
 * page view: there is no retry, so a source that genuinely has no favicon costs exactly one request
 * rather than the three spread over fifteen seconds this component used to make.
 *
 * Memoized because it renders once per citation row: the URL derivation is keyed on the domain and
 * the requested size, and the component itself skips re-rendering while those and `api` are unchanged.
 *
 * `size` is the rendered edge length in px, defaulting to the constant every caller used before it
 * existed — a caller that does not pass one renders exactly what it always did.
 *
 * `requestSize` is what Sync Core is ASKED for, defaulting to twice the rendered size. A caller
 * drawing the same logo at two sizes passes one value for both, so the page asks for one icon rather
 * than two: an icon Sync Core has not cached is fetched synchronously on the request that asks for
 * it, and every extra size is another cold fetch through a four-slot queue the whole page shares.
 * Either way the figure is snapped up to a size the endpoint serves (`faviconRequestSize`) before it
 * is asked for, so a caller passes the size its LAYOUT wants and never has to know that list.
 *
 * `onLoadedChange` reports whether an icon is on the screen RIGHT NOW — false until one loads, and
 * false again the moment the domain changes. Every other outcome this component can reach is silent
 * by design, and a caller that only knows a domain cannot tell a logo from a queue slot still
 * waiting, a domain with no icon, or a 404. A caller that changes what it draws around the icon
 * needs that distinction, which is why it is a report of the rendered result rather than a guess
 * from the props. Pass a stable callback (`useCallback`) — an inline one re-renders the memo.
 */
export const SourceFavicon = React.memo(function SourceFavicon({
  api,
  domain,
  size = FAVICON_SIZE,
  requestSize,
  onLoadedChange,
}: {
  api?: IFaviconUrlSource;
  domain?: string;
  size?: number;
  requestSize?: number;
  onLoadedChange?: (loaded: boolean) => void;
}) {
  const ready = useContext(FaviconLoadGateContext);

  const url = useMemo(() => {
    const host = domain?.trim();
    if (!host || !api) {
      return '';
    }
    try {
      // Requested at 2x by default, so the icon stays crisp on high-density displays.
      return api.utility.scrape.getFaviconUrl(host, faviconRequestSize(requestSize ?? size * 2));
    } catch {
      return '';
    }
  }, [api, domain, size, requestSize]);

  const [phase, setPhase] = useState<FaviconLoadPhase>('pending');
  const releaseRef = useRef<(() => void) | undefined>(undefined);
  /** The url this instance has already entered into the queue for (or settled for). */
  const requestedUrlRef = useRef<string | undefined>(undefined);

  // A layout effect, deliberately, not the usual `useEffect`: a page with room grants a slot the
  // instant a row mounts, and that grant has to land in the SAME commit the row's placeholder did.
  // `useEffect` runs on its own schedule, after paint; a caller that reads the DOM the moment rows
  // appear (e.g. a test's `waitFor` on the row count, woken by that same mutation) can observe the
  // placeholder a beat before a plain effect would have promoted it, even though a slot was free the
  // whole time. `useLayoutEffect` closes that gap by running synchronously as part of the commit.
  useLayoutEffect(() => {
    if (requestedUrlRef.current !== url) {
      // A new domain: whatever the previous one held — granted or merely queued — is no longer ours
      // to keep, and the memoized element starts this one fresh rather than carrying over the last
      // domain's outcome. `ready` toggling on its own, with the domain unchanged, does NOT reach
      // here, so a load already in flight or already shown is never disturbed by a later refetch
      // closing the gate again.
      releaseRef.current?.();
      releaseRef.current = undefined;
      requestedUrlRef.current = undefined;
      setPhase('pending');
    }

    if (!url || !ready || requestedUrlRef.current === url) {
      // Nothing to load, the screen is not ready yet, or this exact url is already queued or settled
      // — `ready` flipping again must not enter it into the queue a second time.
      return;
    }

    requestedUrlRef.current = url;
    releaseRef.current = requestFaviconLoadSlot(() => setPhase('loading'));
  }, [url, ready]);

  // Unmount safety net: whatever slot this instance still holds — granted or only queued — is freed
  // for the rest of the page rather than held forever by a row that is no longer on screen.
  useLayoutEffect(() => () => releaseRef.current?.(), []);

  // Held in a ref and fired on the PHASE alone, so a caller that passes a fresh closure each render
  // is told once per real change rather than once per render of its own.
  const onLoadedChangeRef = useRef(onLoadedChange);
  useEffect(() => {
    onLoadedChangeRef.current = onLoadedChange;
  });
  useEffect(() => {
    onLoadedChangeRef.current?.(phase === 'loaded');
  }, [phase]);

  if (phase !== 'loading' && phase !== 'loaded') {
    return <span className="flex-shrink-0" style={{ width: size, height: size }} aria-hidden />;
  }

  return (
    <img
      src={url}
      alt=""
      aria-hidden
      width={size}
      height={size}
      className="flex-shrink-0 rounded-sm"
      style={{ width: size, height: size, objectFit: 'contain' }}
      onLoad={() => {
        setPhase('loaded');
        releaseRef.current?.();
      }}
      onError={() => {
        setPhase('failed');
        releaseRef.current?.();
      }}
    />
  );
});

/**
 * The favicon of a tracked entity, rendered before its name.
 *
 * Renders NOTHING when the domain is unknown — no spacer, unlike the source variant. Sync Core's
 * domain inference abstains far more often than it answers, so "unknown" is the common case, and
 * reserving a blank box before most names would leave a permanent gutter of dead space. Names
 * simply start where they always did.
 *
 * Muting is inherited rather than passed: every call site already applies the term's status classes
 * (`opacity-50`) to the row, chip or button containing this, and opacity applies down the whole
 * subtree. A separate muting prop here could disagree with the name beside it.
 */
export const EntityFavicon = React.memo(function EntityFavicon({ api, domain }: { api?: IFaviconUrlSource; domain?: string }) {
  if (!domain?.trim()) {
    return null;
  }

  return <SourceFavicon api={api} domain={domain} />;
});
